import { useEffect, useState } from "react";
import { Check, Loader2, Crown, Sparkles, CalendarClock, Gift, Tag, X } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const ACCENT = {
  basic: { ring: "border-blue-600/50", badge: "bg-blue-500/15 text-blue-300", btn: "bg-blue-600 hover:bg-blue-500", icon: Sparkles },
  premium: { ring: "border-amber-500/60", badge: "bg-amber-500/15 text-amber-300", btn: "bg-amber-500 hover:bg-amber-400 text-black", icon: Crown },
};

export default function Pricing() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [data, setData] = useState({ plans: [], trial_days: 3 });
  const [interval, setInterval] = useState("annual");
  const [busy, setBusy] = useState(null);
  const [skipTrial, setSkipTrial] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => {
    api.get(`/payments/plans?lang=${lang}`).then(({ data }) => setData(data)).catch(() => {});
  }, [lang]);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      const { data } = await api.get(`/payments/validate-promo?code=${encodeURIComponent(code)}&plan_id=premium&interval=${interval}`);
      if (data.valid) {
        setPromo({ code: data.code, percent_off: data.percent_off });
        toast.success(t("pr.promoOk", { code: data.code, pct: data.percent_off }));
      } else {
        setPromo(null);
        toast.error(data.message || t("pr.promoInvalid"));
      }
    } catch (e) {
      setPromo(null);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPromoBusy(false); }
  };

  const clearPromo = () => { setPromo(null); setPromoInput(""); };

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/payments/checkout", { plan_id: planId, interval, origin_url: window.location.origin, promo_code: promo?.code || null, skip_trial: skipTrial });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(null);
    }
  };

  const canTrial = user && !user.trial_used && user.plan === "free";

  return (
    <div>
      <PageHeader title={t("pr.title")} subtitle={t("pr.sub")} icon={Crown} />

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-[#11141A] px-4 py-3 text-sm text-slate-400">
        <CalendarClock className="h-4 w-4 text-slate-500" />
        {t("pr.currentPlan")} <span className="font-semibold text-white" data-testid="current-plan-badge">{t(`plan.${user?.plan || "free"}`)}</span>
        {user?.is_trial && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-xs font-semibold text-emerald-300">{t("pr.trialOn")}</span>}
      </div>

      {canTrial && (
        <div className="mb-6 flex flex-col items-start gap-3 rounded-xl border border-emerald-700/40 bg-emerald-950/20 p-4" data-testid="trial-banner">
          <div className="flex items-center gap-3">
            <Gift className="h-6 w-6 text-emerald-400" />
            <div>
              <div className="font-semibold text-white">{t("pr.tryFree1")} {data.trial_days} {t("pr.tryFree2")}</div>
              <div className="text-xs text-slate-400">{t("pr.trialDesc")}</div>
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-300" data-testid="skip-trial-toggle">
            <input type="checkbox" checked={skipTrial} onChange={(e) => setSkipTrial(e.target.checked)}
              className="h-4 w-4 rounded border-slate-600 bg-slate-900 accent-amber-500" data-testid="skip-trial-checkbox" />
            {t("pr.skipTrial")}
          </label>
        </div>
      )}

      {/* Bascule mensuel / annuel */}
      <div className="mb-8 flex items-center justify-center gap-3">
        <div className="inline-flex items-center rounded-full border border-slate-800 bg-[#11141A] p-1" data-testid="interval-toggle">
          <button onClick={() => setInterval("monthly")} data-testid="interval-monthly"
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${interval === "monthly" ? "bg-slate-700 text-white" : "text-slate-400"}`}>
            {t("pr.monthly")}
          </button>
          <button onClick={() => setInterval("annual")} data-testid="interval-annual"
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${interval === "annual" ? "bg-amber-500 text-black" : "text-slate-400"}`}>
            {t("pr.annual")}
          </button>
        </div>
      </div>

      {/* Code promo */}
      <div className="mx-auto mb-8 flex max-w-md flex-col items-center gap-2" data-testid="promo-section">
        {promo ? (
          <div className="flex w-full items-center justify-between rounded-lg border border-emerald-700/50 bg-emerald-950/20 px-4 py-2.5" data-testid="promo-applied">
            <span className="flex items-center gap-2 text-sm text-emerald-300"><Tag className="h-4 w-4" /> {t("acc.promoCode")} <b>{promo.code}</b> · −{promo.percent_off}%</span>
            <button onClick={clearPromo} data-testid="promo-clear" className="text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex w-full gap-2">
            <input value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder={t("pr.promoPh")}
              data-testid="promo-input"
              className="flex-1 rounded-lg border border-slate-700 bg-[#11141A] px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
            <button onClick={applyPromo} disabled={promoBusy || !promoInput.trim()} data-testid="promo-apply"
              className="flex items-center gap-2 rounded-lg bg-slate-700 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-600 disabled:opacity-60">
              {promoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />} {t("pr.apply")}
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-5 md:grid-cols-2 lg:max-w-3xl">
        {data.plans.map((p) => {
          const a = ACCENT[p.id] || ACCENT.basic;
          const Icon = a.icon;
          const current = user?.plan === p.id && !user?.is_trial;
          const price = interval === "annual" ? p.annual : p.monthly;
          const pct = promo?.percent_off || 0;
          const discounted = pct ? (price * (1 - pct / 100)) : null;
          const monthlyEq = interval === "annual" ? (p.annual / 12).toFixed(2) : null;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`} className={`relative rounded-2xl border ${a.ring} bg-[#0E1118] p-6`}>
              {p.id === "premium" && <span className="absolute -top-3 right-5 rounded-full bg-amber-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-black">{t("pr.recommended")}</span>}
              <div className={`mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${a.badge}`}>
                <Icon className="h-3.5 w-3.5" /> {p.label}
              </div>
              <div className="mb-1 flex items-end gap-1">
                {discounted != null && <span className="mb-1 font-heading text-2xl font-bold text-slate-600 line-through">${price}</span>}
                <span className="font-heading text-4xl font-bold text-white" data-testid={`price-${p.id}`}>${(discounted != null ? discounted : price).toFixed(2)}</span>
                <span className="mb-1 text-sm text-slate-500">/{interval === "annual" ? t("pr.perYear") : t("pr.perMonth")}</span>
              </div>
              {discounted != null && <p className="mb-2 text-xs font-semibold text-emerald-400">{t("pr.codeApplied", { code: promo.code, pct })}</p>}
              {interval === "annual" ? (
                <p className="mb-3 text-xs text-emerald-400">{t("pr.perMonthApprox", { eq: monthlyEq, pct: p.annual_savings_pct, save: (p.monthly * 12 - p.annual).toFixed(2) })}</p>
              ) : (
                <p className="mb-3 text-xs text-slate-500">{t("pr.orYear", { price: p.annual, pct: p.annual_savings_pct })}</p>
              )}
              <ul className="my-4 space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {f}
                  </li>
                ))}
              </ul>
              <button data-testid={`subscribe-${p.id}`} disabled={busy || current} onClick={() => subscribe(p.id)}
                className={`flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-60 ${a.btn}`}>
                {busy === p.id && <Loader2 className="h-4 w-4 animate-spin" />}
                {current ? t("pr.currentPlanBtn") : ((canTrial && !skipTrial) ? t("pr.trialCta", { days: data.trial_days, label: p.label }) : `${t("pr.choose")} ${p.label}`)}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
